import type { TurnEvent } from "@procuremate/shared";
import { errorFromResponse } from "@/lib/api-client";

const KNOWN_EVENTS = new Set<string>(["turn.started", "reply.delta", "case.updated", "turn.done", "turn.error"]);

/**
 * Splits server-sent-event text into complete events and the unfinished rest (kept for the
 * next chunk). Only `event:` and `data:` lines matter here; unknown events are skipped.
 */
export function parseSse(buffer: string): { events: TurnEvent[]; rest: string } {
  const blocks = buffer.replace(/\r\n?/g, "\n").split("\n\n");
  const rest = blocks.pop() ?? "";
  const events: TurnEvent[] = [];
  for (const block of blocks) {
    let name = "";
    const data: string[] = [];
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    }
    if (!KNOWN_EVENTS.has(name) || data.length === 0) continue;
    try {
      events.push({ event: name, data: JSON.parse(data.join("\n")) } as TurnEvent);
    } catch {
      // A malformed event is skipped; the turn's final state comes from `turn.done` / a refetch.
    }
  }
  return { events, rest };
}

/**
 * POST /api/requests/:id/turns and reports each event as it arrives (TurnEvent in
 * packages/shared). Resolves when the stream ends; throws ApiRequestError for errors answered
 * before the stream starts (404, 403, 409, 400) and the fetch error for network failures.
 */
export async function streamTurn(
  requestId: string,
  message: string,
  onEvent: (event: TurnEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`/api/requests/${requestId}/turns`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ message }),
    signal,
  });
  if (!response.ok) throw await errorFromResponse(response);
  if (!response.body) throw new Error("The reply stream is not readable.");

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    const parsed = parseSse(buffer + value);
    buffer = parsed.rest;
    for (const event of parsed.events) onEvent(event);
  }
  for (const event of parseSse(`${buffer}\n\n`).events) onEvent(event);
}
